import * as React from 'react';
import { FormTextField, TextFieldProps } from 'omakase';
import { core } from '@tock/coretypes';
import { normalizeSmsPhoneStatusType } from 'utils/smsUtils';
import { useActions } from '@tock/shared/utils/connect';
import { useFormikContext } from 'formik';
import { useSelectInputClasses } from 'ui/TextField';
import _ from 'lodash';

interface PhoneValues {
  phone: string;
  phoneCountryCode: string;
}

type FormPhoneFieldOwnProps = {
  className?: string;
  id?: string;
  label?: string;
  name?: string;
  onBlur?(evt: React.FocusEvent<HTMLInputElement>): void;
  placeholder?: string;
  warningAsError?: boolean;
  setHasPhoneError?: (hasPhoneError: boolean) => void;
  onValidatePhone?(status: core.SmsPhone.StatusType | undefined): void;
  onPhoneValidationComplete?(payload: {
    phone: string;
    phoneCountryCode: string;
    status: core.SmsPhone.StatusType | undefined;
  }): void;
};

export type FormPhoneFieldProps = FormPhoneFieldOwnProps & TextFieldProps;

/**
 * Formik-ready text input that validates entered value as a phone number
 */
const FormPhoneField: React.FunctionComponent<FormPhoneFieldProps> = ({
  helperText,
  id = 'phone',
  label = 'Phone number',
  name = 'phone',
  onBlur,
  onPhoneValidationComplete,
  onValidatePhone,
  placeholder = 'Phone number',
  warningAsError = false,
  setHasPhoneError,
  ...rest
}) => {
  const {
    actions: {
      patron: { validatePhoneRequest },
    },
  } = useActions();

  const classes = useSelectInputClasses();

  const { values } = useFormikContext<PhoneValues>();

  const { phone, phoneCountryCode } = values;

  const [warn, setPhoneWarning] = React.useState<string | undefined>();
  const [valid, setPhoneValid] = React.useState<string | undefined>();

  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const validate = React.useCallback(
    async ({ phone, phoneCountryCode }) => {
      if (_.size(phone) === 0 || _.size(phoneCountryCode) === 0) {
        return;
      }
      let status: core.SmsPhone.StatusType | undefined;
      if (mountedRef.current) {
        setPhoneWarning(undefined);
      }

      try {
        const response = await validatePhoneRequest({ phone, phoneCountryCode });
        const result: core.ValidatePhoneResponse = response.payload;

        status = normalizeSmsPhoneStatusType(result.smsPhoneConsentStatus);
        onValidatePhone?.(status);

        if (!result.isValid) {
          const warningText = 'Hmmm, we could not validate this phone number';
          if (mountedRef.current) {
            setPhoneWarning(warningText);
          }
          if (mountedRef.current) {
            setPhoneValid(undefined);
          }
          if (mountedRef.current) {
            setHasPhoneError?.(true);
          }
        } else {
          if (mountedRef.current) {
            setPhoneValid(
              `${_.first(phoneCountryCode.split(' '))} ${
                response.payload.beautifiedPhone
              } is a valid number`
            );
          }
          if (mountedRef.current) {
            setPhoneWarning(undefined);
          }
          if (mountedRef.current) {
            setHasPhoneError?.(false);
          }
        }
      } finally {
        onPhoneValidationComplete?.({ phone, phoneCountryCode, status });
      }
    },
    [onPhoneValidationComplete, onValidatePhone, setHasPhoneError, validatePhoneRequest]
  );

  React.useEffect(() => {
    validate({ phone, phoneCountryCode });
    // this is to separate change from blur.
    // we only want this to happen when the country code changes, which is outside of this component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneCountryCode, validate]);

  const handlePhoneBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const newPhone = _.trim(event.target.value);

    if (onBlur) {
      onBlur(event);
    }

    validate({ phone: newPhone, phoneCountryCode: phoneCountryCode });
  };

  return (
    <FormTextField
      warn={warn && !warningAsError ? true : undefined}
      error={warn && warningAsError ? true : undefined}
      valid={valid ? true : undefined}
      helperText={warn || valid || helperText}
      onBlur={handlePhoneBlur}
      className={classes.root}
      id={id}
      name={name}
      label={label}
      placeholder={placeholder}
      type="tel"
      {...rest}
    />
  );
};
export default FormPhoneField;
